import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { DashboardLayout } from './DashboardLayout';
import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { ForgotPasswordPage, ResetPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ConsentPage } from '@/features/oauth/ConsentPage';
import { PrivacyPage } from '@/features/legal/PrivacyPage';
import { TermsPage } from '@/features/legal/TermsPage';
import { ConnectPage } from '@/features/legal/ConnectPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { SearchPage } from '@/features/search/SearchPage';
import { VendorsPage } from '@/features/vendors/VendorsPage';
import { VendorPage } from '@/features/vendors/VendorPage';
import { OverviewPage } from '@/features/overview/OverviewPage';
import { landingPath } from '@/features/overview/overview-rows';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { CustomersPage } from '@/features/customers/CustomersPage';
import { InvoicesPage } from '@/features/invoices/InvoicesPage';
import { InvoiceComposerPage, InvoiceEditPage } from '@/features/invoices/InvoiceComposerPage';
import { InvoiceDetailPage } from '@/features/invoices/InvoiceDetailPage';
import { ProformaComposerPage } from '@/features/invoices/InvoiceComposerPage';
import { ProformasPage } from '@/features/proformas/ProformasPage';
import { ProformaDetailPage } from '@/features/proformas/ProformaDetailPage';
import { ExpensesPage } from '@/features/expenses/ExpensesPage';
import { QuickAddPage } from '@/features/quick-add/QuickAddPage';
import { InboxPage } from '@/features/inbox/InboxPage';
import { PaymentsPage } from '@/features/payments/PaymentsPage';
import { BankingPage } from '@/features/banking/BankingPage';
import { ItemsPage } from '@/features/items/ItemsPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { AccountPage } from '@/features/settings/AccountPage';
import { MembersPage } from '@/features/settings/MembersPage';
import { OrganisationPage } from '@/features/settings/OrganisationPage';
import { RecurringPage } from '@/features/recurring/RecurringPage';
import { TagsPage } from '@/features/settings/TagsPage';
import { CategoriesPage } from '@/features/settings/CategoriesPage';
import { GoalsPage } from '@/features/settings/GoalsPage';
import { ProjectsPage } from '@/features/projects/ProjectsPage';
import { PaletteStyles } from '@/features/common/ui';
import { ThemeProvider } from '@/features/common/ThemeProvider';
import { PracticePage } from '@/features/practice/PracticePage';
import { ReviewPage } from '@/features/review/ReviewPage';
import { GstReturnsPage } from '@/features/gst/GstReturnsPage';
import { ExportsPage } from '@/features/exports/ExportsPage';
import { CfoPage } from '@/features/cfo/CfoPage';
import { CustomerLedgerPage } from '@/features/ledger/CustomerLedgerPage';
import { AuditPage } from '@/features/audit/AuditPage';
import { InstallBanner } from '@/features/install/InstallBanner';
import { UpdateBar } from '@/features/install/UpdateBar';
import { StaleAppReload } from '@/features/install/StaleAppReload';

/**
 * Routes.
 *
 * `ProtectedRoute` reads the PERSISTED auth hint, not a server check - it only
 * decides whether to render the shell or bounce to /login. The real check is on
 * every API request; if the session is actually dead, the first call 401s and
 * lib/api.ts redirects. Gating on a network round trip instead would mean a
 * blank screen on every page load.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

/**
 * A signed-in person on a guest page is sent where sign-in would have sent
 * them - not to "/".
 *
 * ── THE RACE THIS SETTLES ──
 * LoginPage does `setSession(...)` and then `navigate(landingPath(...))`, one
 * line apart. `setSession` flips `isAuthenticated`, and the store's
 * subscribers re-render synchronously (useSyncExternalStore) - so THIS
 * component renders, with the location still `/login`, before the next line
 * runs. It used to answer `<Navigate to="/" />`. Two navigations were then in
 * flight and the second to land won, which was this one: a person with two
 * books reached `/overview` for one frame and then found themselves on a
 * single-book dashboard, with no error anywhere. Invisible for a one-book
 * person, whose landing path IS "/", which is why it shipped.
 *
 * Sending them to `landingPath` instead makes both navigations agree, so the
 * order no longer matters. The same protects `?next=`: LoginPage still owns
 * that, and this only fires when the person is already signed in.
 */
function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const orgCount = useAuthStore((s) => s.orgs.length);
  return isAuthenticated ? <Navigate to={landingPath(orgCount)} replace /> : <>{children}</>;
}

export function App() {
  return (
    <>
      {/* Keeps <html class="dark"> in step with the stored choice after the
          inline script in index.html has made the first call. Above the
          router so it runs on guest routes too - the login page must not be
          the one bright screen. */}
      <ThemeProvider />
      {/* The classification palette, from the one table a test measures. */}
      <PaletteStyles />
      {/* "A new version is ready", above the router: the login page can be
          stale too. See features/install. */}
      <UpdateBar />
      {/* ...and delivers it at the next page change, so an open tab cannot
          run an old build for days. */}
      <StaleAppReload />
      <Routes>
      <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
      <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
      {/* The login page has linked to /forgot-password since it was written.
          Without these two routes the catch-all below swallowed it, so the link
          bounced to the dashboard and the whole reset flow was unreachable. */}
      <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
      <Route path="/reset-password" element={<GuestRoute><ResetPasswordPage /></GuestRoute>} />

      {/*
        The OAuth consent screen, reached by a 302 from /v1/oauth/authorize.

        Outside both wrappers on purpose. Not a `GuestRoute` - it is for a
        SIGNED-IN person, and a guest route would bounce them to the dashboard,
        losing the request id and leaving the assistant hanging. Not inside
        `DashboardLayout` either: this is a full-screen interstitial in a flow
        that started somewhere else, and a sidebar inviting a click away from a
        half-finished authorisation is a way to lose it. It handles its own
        signed-out case by redirecting to login with a validated `next`.
      */}
      <Route path="/oauth/consent" element={<ConsentPage />} />

      {/* Public, signed in or not: the directories that list Fint's connector
          fetch these signed out. See features/legal/LegalShell.tsx. */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/docs/mcp" element={<ConnectPage />} />

      <Route
        element={
          <ProtectedRoute>
            <DashboardLayout />
            {/* "Put Fint on your home screen", for a signed-in person only,
                from their third visit. Fixed-position, so a sibling of the
                layout rather than inside it. */}
            <InstallBanner />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        {/* Every business at once. Where a person with more than one lands
            after signing in; redirects to the dashboard for a person with one,
            because a comparison of one thing is not a comparison. */}
        <Route path="/overview" element={<OverviewPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:customerId/ledger" element={<CustomerLedgerPage />} />
        {/* Static before parameterised: /invoices/new must not match :invoiceId. */}
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/new" element={<InvoiceComposerPage />} />
        <Route path="/invoices/:invoiceId/edit" element={<InvoiceEditPage />} />
        <Route path="/invoices/:invoiceId" element={<InvoiceDetailPage />} />
        {/* "new" before ":proformaId", exactly as on invoices - declared the
            other way round the parameter swallows it and the detail page 404s
            on an id that was never an id. */}
        <Route path="/proformas" element={<ProformasPage />} />
        <Route path="/proformas/new" element={<ProformaComposerPage />} />
        <Route path="/proformas/:proformaId" element={<ProformaDetailPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/expenses" element={<ExpensesPage />} />
        {/* Four screens, one question each. The composer on /expenses is the
            full form; this is the one a ten-year-old can use. */}
        <Route path="/add" element={<QuickAddPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/banking" element={<BankingPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/items" element={<ItemsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/vendors/:vendorId" element={<VendorPage />} />
        <Route path="/recurring" element={<RecurringPage />} />
        <Route path="/settings/account" element={<AccountPage />} />
        <Route path="/settings/members" element={<MembersPage />} />
        <Route path="/settings/organisations" element={<OrganisationPage />} />
        <Route path="/settings/tags" element={<TagsPage />} />
        <Route path="/settings/categories" element={<CategoriesPage />} />
        <Route path="/settings/goals" element={<GoalsPage />} />
        <Route path="/projects" element={<ProjectsPage />} />

        {/* The accountant's side of the app. */}
        <Route path="/clients" element={<PracticePage />} />
        <Route path="/review" element={<ReviewPage />} />
        <Route path="/gst" element={<GstReturnsPage />} />
        <Route path="/exports" element={<ExportsPage />} />
        <Route path="/cfo" element={<CfoPage />} />
        <Route path="/activity" element={<AuditPage />} />
      </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
